import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Lock, ArrowRight, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | ready | expired | notfound | done
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) { setState("notfound"); return; }
    (async () => {
      try {
        const { data } = await api.get(`/auth/password-reset/${token}`);
        setEmail(data?.email || "");
        setState("ready");
      } catch (e) {
        if (e?.response?.status === 410) setState("expired");
        else setState("notfound");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Salasanan on oltava vähintään 8 merkkiä");
      return;
    }
    if (password !== confirm) {
      toast.error("Salasanat eivät täsmää");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/auth/password-reset/${token}/confirm`, { password });
      setState("done");
      toast.success("Salasana vaihdettu");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Salasanan vaihto epäonnistui");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-3xl border border-slate-200 shadow-sm p-8 md:p-10" data-testid="reset-password-card">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="reset-back-login">
          <ArrowLeft className="h-4 w-4" /> Takaisin kirjautumiseen
        </Link>

        {state === "loading" && (
          <div className="py-10 flex items-center justify-center text-slate-500" data-testid="reset-loading">
            <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
          </div>
        )}

        {(state === "expired" || state === "notfound") && (
          <div data-testid="reset-invalid">
            <div className="h-14 w-14 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mb-5">
              <AlertTriangle className="h-7 w-7" />
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 leading-tight">
              {state === "expired" ? "Palautuslinkki on vanhentunut" : "Palautuslinkki ei ole voimassa"}
            </h1>
            <p className="text-slate-600 mt-2 leading-relaxed">
              {state === "expired"
                ? "Linkki vanheni 60 minuutin kuluttua lähetyksestä. Pyydä uusi palautuslinkki ja yritä uudelleen."
                : "Linkki on jo käytetty tai sitä ei löydy. Pyydä uusi palautuslinkki."}
            </p>
            <Link to="/unohdin-salasanan" className="btn-primary w-full py-3.5 mt-6" data-testid="reset-request-new">
              Pyydä uusi linkki <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}

        {state === "ready" && (
          <>
            <div className="h-14 w-14 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-5">
              <Lock className="h-7 w-7" />
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 leading-tight">
              Aseta uusi salasana
            </h1>
            <p className="text-slate-600 mt-2">
              Tilille <span className="font-semibold text-slate-900">{email}</span>
            </p>

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Uusi salasana</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-base"
                  placeholder="Vähintään 8 merkkiä"
                  data-testid="reset-password-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Vahvista uusi salasana</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="input-base"
                  placeholder="Toista salasana"
                  data-testid="reset-confirm-input"
                />
              </div>
              <button
                type="submit"
                disabled={busy || password.length < 8 || password !== confirm}
                className="btn-primary w-full py-3.5 disabled:opacity-50"
                data-testid="reset-submit-button"
              >
                {busy ? "Tallennetaan..." : "Tallenna uusi salasana"}
                {!busy && <ArrowRight className="h-4 w-4" />}
              </button>
            </form>
          </>
        )}

        {state === "done" && (
          <div data-testid="reset-done">
            <div className="h-14 w-14 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-5">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 leading-tight">
              Salasana vaihdettu
            </h1>
            <p className="text-slate-600 mt-2 leading-relaxed">
              Voit nyt kirjautua sisään uudella salasanallasi.
            </p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="btn-primary w-full py-3.5 mt-6"
              data-testid="reset-go-to-login"
            >
              Siirry kirjautumissivulle <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
